import { createTheme, responsiveFontSizes } from '@mui/material';

// Single source of truth for the app's surface/shell palette. Mirrors the fields of the
// legacy getColors() map so it can replace it. NOTE: values below intentionally match the
// CURRENT theme — no visual change yet; recolour by editing these in one place.
export interface CustomColors {
  bg: string;
  sidebar: string;
  activeNavGrad: string;
  activeNavSolid: string;
  // Bright brand blue (secondary.main) — used for active/highlighted accents and glows.
  accent: string;
  card: string;
  cardHover: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  iconMuted: string;
  error: string;
  white: string;
  assetsBg: string;
  userBg: string;
  // Subtle tile/overlay fill (e.g. icon tiles) — replaces ad-hoc rgba overlays.
  overlay: string;
}

// extending the default theme object to add custom properties
declare module '@mui/material/styles' {
  interface Theme {
    custom_spacing: number;
    custom_gradient: string;
    custom_colors: CustomColors;
  }

  interface ThemeOptions {
    custom_spacing: number;
    custom_gradient: string;
    custom_colors: CustomColors;
  }

  interface TypographyVariants {
    caption2: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption2?: React.CSSProperties;
  }
}

// Update the Typography's prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    caption2: true;
  }
}

export const darkTheme = responsiveFontSizes(
  createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#1778C7',
      },
      secondary: {
        main: '#46A0EB',
      },
      success: {
        main: '#10B981',
      },
      error: {
        main: '#EF4444',
      },
      warning: {
        main: '#F59E0B',
      },
      info: {
        main: '#2196F3',
        light: '#90CAF9',
        dark: '#1565C0',
      },
      background: {
        default: '#080B11',
        paper: '#131925',
      },
      text: {
        primary: '#E9EFF8',
        secondary: '#9DAABF',
      },
      divider: 'rgba(132,164,212,0.18)',
    },
    typography: {
      caption2: {
        fontSize: '0.625rem',
      },
      allVariants: {
        fontFamily: "'Public Sans', 'Montserrat', arial, sans-serif",
        letterSpacing: '0.1rem',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
          },
          containedPrimary: {
            background: 'linear-gradient(180deg, #1778C7 0%, #46A0EB 100%)',
            ':hover': {
              background: 'linear-gradient(180deg, #1366a9 0%, #2790e8 100%)',
            },
            transition:
              'background 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
          textPrimary: {
            color: 'inherit',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            color: 'white',
            background: 'linear-gradient(180deg, #1778C7 0%, #46A0EB 100%)',
            ':hover': {
              background: 'linear-gradient(180deg, #1366a9 0%, #2790e8 100%)',
            },
            transition:
              'background 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

            '&.Mui-disabled': {
              color: 'rgb(255 255 255 / 0.3)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(rgb(23, 120, 199), rgb(70, 160, 235))',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0px 10px',
            height: '65px',
            position: 'static',
            color: 'white',

            '& .MuiIconButton-root': {
              color: 'white',
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: '1px solid grey',
            '&.Mui-expanded': { margin: '0px' },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 0,
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '&.Mui-expanded': {
              minHeight: 0,
              margin: 0,
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              margin: 0,
            },
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          slotProps: {
            paper: {
              sx: {
                maxHeight: '40dvh',
                padding: '5px',
                margin: '0px',
                borderRadius: '0px',
                overflow: 'auto',
                marginInline: '-5px',
                paddingInline: '5px',
                '& .MuiMenu-list': {
                  padding: '0px',
                  margin: '0px',
                },
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '& .MuiSvgIcon-root': { fontSize: 30 },
          },
        },
      },
      MuiListItemText: {
        defaultProps: {
          slotProps: {
            primary: {
              variant: 'caption',
            },
            secondary: {
              variant: 'caption2',
            },
          },
        },
      },
      MuiMenuItem: {
        defaultProps: {
          sx: {
            minHeight: { xs: '32px', sm: '40px' },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 20,
            padding: 0,
            marginLeft: 'auto',
          },
          switchBase: ({ theme }) => ({
            padding: 0,
            margin: '2px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#2ECA45',
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: '#33cf4d',
              border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              color: theme.palette.grey[600],
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.3,
            },
          }),
          thumb: {
            boxSizing: 'border-box',
            width: 16,
            height: 16,
          },
          track: ({ theme }) => ({
            borderRadius: 20 / 2,
            backgroundColor: '#39393D',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
              duration: 500,
            }),
          }),
        },
      },
    },
    custom_spacing: 5,
    custom_gradient: 'linear-gradient(rgb(23, 120, 199), rgb(70, 160, 235))',
    // Dark shell colors — TheBPR.ai navy theme (matches the login page).
    custom_colors: {
      bg: '#080B11',
      sidebar: '#0C1018',
      activeNavGrad: 'linear-gradient(180deg, #1778C7 0%, #46A0EB 100%)',
      activeNavSolid: '#1778C7',
      accent: '#46A0EB',
      card: '#131925',
      cardHover: '#1A2230',
      border: 'rgba(132,164,212,0.18)',
      textPrimary: '#E9EFF8',
      textSecondary: '#9DAABF',
      iconMuted: '#62708A',
      error: '#EF4444',
      white: '#FFFFFF',
      assetsBg: '#1A2230',
      userBg: '#1A2230',
      overlay: 'rgba(255,255,255,0.05)',
    },
  })
);

export const lightTheme = responsiveFontSizes(
  createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#1778C7',
      },
      secondary: {
        main: '#46A0EB',
      },
      success: {
        main: '#10B981',
      },
      error: {
        main: '#EF4444',
      },
      warning: {
        main: '#F59E0B',
      },
      info: {
        main: '#2196F3',
        light: '#90CAF9',
        dark: '#1565C0',
      },
      background: {
        default: '#FFFFFF',
        paper: '#f1f1f1ff',
      },
    },
    typography: {
      caption2: {
        fontSize: '0.625rem',
      },
      allVariants: {
        fontFamily: "'Public Sans', 'Montserrat', arial, sans-serif",
        letterSpacing: '0.1rem',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
          },
          containedPrimary: {
            background: 'linear-gradient(180deg, #1778C7 0%, #46A0EB 100%)',
            ':hover': {
              background: 'linear-gradient(180deg, #1366a9 0%, #2790e8 100%)',
            },
            transition:
              'background 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
          textPrimary: {
            color: 'inherit',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            color: 'white',
            background: 'linear-gradient(180deg, #1778C7 0%, #46A0EB 100%)',
            ':hover': {
              background: 'linear-gradient(180deg, #1366a9 0%, #2790e8 100%)',
            },
            transition:
              'background 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

            '&.Mui-disabled': {
              color: 'rgb(255 255 255 / 0.3)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(rgb(23, 120, 199), rgb(70, 160, 235))',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0px 10px',
            height: '65px',
            position: 'static',

            '& .MuiIconButton-root': {
              color: 'white',
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: '1px solid grey',
            '&.Mui-expanded': { margin: '0px' },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 0,
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '&.Mui-expanded': {
              minHeight: 0,
              margin: 0,
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              margin: 0,
            },
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          slotProps: {
            paper: {
              sx: {
                maxHeight: '40dvh',
                padding: '5px',
                margin: '0px',
                borderRadius: '0px',
                overflow: 'auto',
                marginInline: '-5px',
                paddingInline: '5px',
                '& .MuiMenu-list': {
                  padding: '0px',
                  margin: '0px',
                },
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '& .MuiSvgIcon-root': { fontSize: 30 },
          },
        },
      },
      MuiListItemText: {
        defaultProps: {
          slotProps: {
            primary: {
              variant: 'caption',
            },
            secondary: {
              variant: 'caption2',
            },
          },
        },
      },
      MuiMenuItem: {
        defaultProps: {
          sx: {
            minHeight: { xs: '32px', sm: '40px' },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 20,
            padding: 0,
            marginLeft: 'auto',
          },
          switchBase: ({ theme }) => ({
            padding: 0,
            margin: '2px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#65c466',
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: '#33cf4d',
              border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              color: theme.palette.grey[100],
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.7,
            },
          }),
          thumb: {
            boxSizing: 'border-box',
            width: 16,
            height: 16,
          },
          track: ({ theme }) => ({
            borderRadius: 20 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
              duration: 500,
            }),
          }),
        },
      },
    },
    custom_spacing: 5,
    custom_gradient: 'linear-gradient(rgb(23, 120, 199), rgb(70, 160, 235))',
    // Current light shell colors (unchanged from getColors).
    custom_colors: {
      bg: '#F5F5F5',
      sidebar: '#FFFFFF',
      activeNavGrad: 'linear-gradient(180deg, #1778C7 0%, #46A0EB 100%)',
      activeNavSolid: '#1778C7',
      accent: '#46A0EB',
      card: '#F1F1F1',
      cardHover: '#E8E8E8',
      border: '#DDDDDD',
      textPrimary: '#1a1a1a',
      textSecondary: '#555555',
      iconMuted: '#888888',
      error: '#EF4444',
      white: '#1a1a1a',
      assetsBg: '#EBEBEB',
      userBg: '#EBEBEB',
      overlay: 'rgba(0,0,0,0.04)',
    },
  })
);
